import React from 'react'

import { Breakpoint, pluralize, useModalContext } from '@sketch/components'

import {
  GetStorageItemsDocument,
  GetStorageItemsQuery,
  ProjectDetailsFragment,
  ShareListItemFragment,
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import {
  BaseStorageItemsLayoutProps,
  StorageItemsLayout,
} from '../../../storageItems/components'
import { useSortingSettings } from '../../../shares/hooks/useSortSettings'
import { isTouchDevice, useSearch } from '@sketch/utils'
import {
  DocumentGridItemPlaceholder,
  DocumentTableItemPlaceholder,
} from '../../../shares/components/DocumentItem'
import {
  ActionTableCell,
  FocusLink,
  HeaderTableCell,
} from '../../../shares/components/DocumentItemsLayout/DocumentItemsLayout.styles'
import { ResponsiveValues } from '@sketch/global-styles'
import {
  isDraftsRoute,
  isProjectRoute,
  routes,
  useFlag,
  useUserProfile,
} from '@sketch/modules-common'
import { useHistory, useRouteMatch } from 'react-router'
import EmptyState from '../../../shares/components/EmptyStateOld'
import { ErrorHandler } from '@sketch/tracing'
import {
  DocumentItemDropdown,
  DocumentItemDropdownProps,
} from '../../../shares/components/DocumentItemDropdown'
import { updatePinnedShares } from '../../../shares/components/ShareActionsDropdown/pinShare.cache'
import { ShareTrashedModal } from '../../../shares/components/ShareTrashedModal'
import {
  DraggableShareItem,
  DraggableShareItemProps,
} from '../../../shares/components/DraggableShareItem'
import { useDropStorageItemsIntoShare } from '../../../shares/hooks/useDropStorageItemsIntoShare'
import { useSelectionContext } from '../../../storageItems/components/SelectionContext'

import {
  GetStorageItemsProps,
  normalizePropsToVariables,
} from '../../hooks/useProject'

interface ProjectDocumentListProps
  extends BaseStorageItemsLayoutProps<ShareListItemFragment> {
  workspace: WorkspaceMinimalFragment
  project: ProjectDetailsFragment
  action?: (item: StorageItemFragment) => (() => void) | 'link'
  queryVariables: GetStorageItemsProps
}

type DocumentItemDropdownShare = DocumentItemDropdownProps['share']

export const ProjectDocumentList = (props: ProjectDocumentListProps) => {
  const {
    workspace,
    project,
    totalCount = 0,
    onLoadMore,
    items,
    customColumns,
    queryVariables,
  } = props

  const history = useHistory()

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  // Get the User Identifier to show the "You" label
  const { data: userData } = useUserProfile()
  const userIdentifier = userData?.me.identifier

  const isDesignSystemsOn = useFlag('design-systems')

  const { showModal } = useModalContext()
  const { sort } = useSortingSettings()
  const { searchDebounced: search } = useSearch()

  // Route match
  const { path } = useRouteMatch()

  const isProjectView = isProjectRoute(path)
  const isDraftView = isDraftsRoute(path)
  const isValidViewToCreateProject = isProjectView || isDraftView

  const isWorkspaceLibrariesRoute = useRouteMatch({
    path: routes.WORKSPACE_LIBRARIES.template(),
    exact: true,
  })

  const dropStorageItems = useDropStorageItemsIntoShare({
    projectIdentifier: project.identifier,
    storageItemsQueryVariables: normalizePropsToVariables(queryVariables, sort),
  })

  const handleStorageItemsDropped = (
    droppedItem: StorageItemFragment,
    relatedItems: StorageItemFragment[]
  ) => {
    // Given we are on the Document Item, if the related items contains shares and projects
    // we are going to create a new project with then.
    dropStorageItems([...relatedItems, droppedItem])
  }

  // Selection
  const { toggleItemSelection, showSelectionCheckbox, isSelected } =
    useSelectionContext()

  /**
   * This action is triggered when the user clicks the DocumentItem
   */
  const actionCreator = (item: StorageItemFragment) => {
    const isShare = item.__typename === 'Share'
    if (isShare && item.deletedAt) {
      return () => showModal(ShareTrashedModal, { share: item })
    }

    return 'link'
  }

  const renderDropdown = (item: StorageItemFragment) => {
    // Rendering a Share Item
    const share = items.find(
      share => share.identifier === item.identifier
    ) as DocumentItemDropdownShare

    if (!share) {
      ErrorHandler.shouldNeverHappen(
        'share should always be present rendering the ProjectDocumentDropdown'
      )
      return null
    }

    return (
      <DocumentItemDropdown
        share={share}
        workspaceIdentifier={workspace.identifier}
        workspace={workspace}
        pinDocumentConfig={{
          shareIdentifier: share.identifier,
          pinned: !!share?.pinnedByCurrentUserAt,
          update(cache, { data }) {
            if (!data) return

            updatePinnedShares<GetStorageItemsQuery>({
              shareId: share.identifier,
              pickPagination: data => data?.project?.storageItems,
              readData: () => {
                const result = cache.readQuery<GetStorageItemsQuery>({
                  query: GetStorageItemsDocument,
                  variables: queryVariables,
                })
                return result ?? undefined
              },
              writeData: newData => {
                cache.writeQuery({
                  query: GetStorageItemsDocument,
                  variables: queryVariables,
                  data: newData,
                })
              },
              sort,
            })
          },
        }}
      />
    )
  }

  const isTouch = isTouchDevice()

  const renderGridItem = (item: ShareListItemFragment, isMobile: boolean) => {
    const action = actionCreator(item)
    const userCanUpdateSettings =
      item.__typename === 'Share' && item.userCanUpdateSettings

    if (!item) return null

    const commonDraggableShareItemProps: DraggableShareItemProps<ShareListItemFragment> =
      {
        presentation: 'grid',
        workspaceIdentifier: workspace.identifier,
        userIdentifier: userIdentifier,
        item: item,
        renderDropdown,
        draggable:
          !(isTouch || isMobile) &&
          userCanUpdateSettings &&
          isValidViewToCreateProject,
        onStorageItemsDropped: draggedItems =>
          handleStorageItemsDropped(item, draggedItems),
        selected: isSelected(item),
        onSelectChange(state: boolean) {
          toggleItemSelection(item, state)
        },
        showSelectionCheckbox,
      }

    const element = showSelectionCheckbox ? (
      <DraggableShareItem
        {...commonDraggableShareItemProps}
        onClick={() => toggleItemSelection(item)}
      />
    ) : (
      <DraggableShareItem
        {...commonDraggableShareItemProps}
        onClick={action !== 'link' ? action : undefined}
      />
    )

    const shouldWrapInLink = !showSelectionCheckbox && action === 'link'

    /**
     * Sadly, we need to wrap the element with a FocusLink even if we don't have to navigate
     * because when changing to the selection mode, react will unmount the DraggableShareItem and mount it again
     * causing issues with image loader as pointed out [here](https://github.com/sketch-hq/cloud-frontend/pull/9710#discussion_r2624157807)
     */
    return (
      <FocusLink
        key={item.identifier}
        draggable={false}
        to={{
          pathname: routes.SHARE_VIEW.create({
            shareID: item.identifier,
          }),
          state: { componentsView: true },
        }}
        onClick={
          /* Prevent the link action from happening when not shouldWrapInLink */
          !shouldWrapInLink ? event => event.preventDefault() : undefined
        }
      >
        {element}
      </FocusLink>
    )
  }

  const handleListItemClick = (
    event: React.MouseEvent<HTMLElement>,
    item: StorageItemFragment
  ) => {
    const link = routes.SHARE_VIEW.create({
      shareID: item.identifier,
    })

    if (event.metaKey) {
      /* Simulate the link opening with meta key */
      window.open(link, '_blank')
    } else {
      history.push(link)
    }
  }

  const renderListItem = (item: ShareListItemFragment, isMobile: boolean) => {
    const action = actionCreator(item)

    const userCanUpdateSettings =
      item.__typename === 'Share' && item.userCanUpdateSettings

    return (
      <DraggableShareItem
        key={item.identifier}
        presentation="list"
        workspaceIdentifier={workspace.identifier}
        userIdentifier={userIdentifier}
        item={item}
        renderDropdown={renderDropdown}
        draggable={
          !isMobile && userCanUpdateSettings && isValidViewToCreateProject
        }
        onStorageItemsDropped={draggedItems =>
          handleStorageItemsDropped(item, draggedItems)
        }
        onClick={event =>
          action === 'link' ? handleListItemClick(event, item) : action()
        }
        selected={isSelected(item)}
        onSelectChange={() => {
          toggleItemSelection(item)
        }}
      />
    )
  }

  const renderLayout = (
    isMobile: boolean,
    customColumns?: ResponsiveValues<number>
  ) => {
    // for design systems search: check if we're on the library route and if empty count for a special message
    if (isWorkspaceLibrariesRoute && totalCount === 0 && isDesignSystemsOn) {
      return (
        <EmptyState
          search={search}
          title="No Libraries"
          description="No Libraries added yet."
          icon="link"
        />
      )
    }

    const commonStorageItemsLayoutProps = {
      items: items,
      onLoadMore: onLoadMore,
      customColumns: customColumns,
      totalCount: totalCount,
    }

    if (documentsLayout === 'LIST') {
      const headerLabel = pluralize('Document', 'Documents', totalCount)

      return (
        <StorageItemsLayout
          header={[
            {
              label: `${totalCount} ${headerLabel}`,
              customCell: HeaderTableCell,
            },
            { label: 'Folder', customCell: HeaderTableCell },
            { label: 'Last Updated', customCell: HeaderTableCell },
            { label: 'Updated By', customCell: HeaderTableCell },
            { label: '', customCell: ActionTableCell },
          ]}
          Skeleton={DocumentTableItemPlaceholder}
          renderItem={item => renderListItem(item, isMobile)}
          {...commonStorageItemsLayoutProps}
        />
      )
    }

    return (
      <StorageItemsLayout
        Skeleton={DocumentGridItemPlaceholder}
        renderItem={item => renderGridItem(item, isMobile)}
        {...commonStorageItemsLayoutProps}
      />
    )
  }

  return (
    <>
      <Breakpoint on="sm">
        {matches => renderLayout(!matches, customColumns)}
      </Breakpoint>
    </>
  )
}
